import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { differenceInDays, format, subDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface DistributionData {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface StudyAdherenceDistributionProps {
  studyId: string;
}

const StudyAdherenceDistribution: React.FC<StudyAdherenceDistributionProps> = ({
  studyId,
}) => {
  const [chartData, setChartData] = useState<DistributionData[]>([]);
  const [displayData, setDisplayData] = useState<DistributionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [showUsageHint, setShowUsageHint] = useState(false);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalParticipants, setTotalParticipants] = useState(0);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchDistributionData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    if (!studyId) {
      setError("Missing studyId");
      setLoading(false);
      return;
    }

    try {
      setHasRun(true);
      setLoading(true);
      setError(null);

      const response = await apiService.get(
        `${API_ENDPOINTS.STUDY_ADHERENCE_MONITOR.PARTICIPANT_DISTRIBUTION}?studyId=${studyId}&startDate=${start}&endDate=${end}`,
      );

      if (response.success) {
        const { distribution, totalParticipants } = response;
        setTotalParticipants(totalParticipants || 0);

        const data: DistributionData[] = [
          {
            name: "Excellent: >90%",
            value: distribution?.excellent || 0,
            color: "#4CAF50",
          },
          {
            name: "Good: 80-90%",
            value: distribution?.good || 0,
            color: "#00505C",
          },
          {
            name: "Fair: 70-80%",
            value: distribution?.fair || 0,
            color: "#7C3AED",
          },
          {
            name: "Poor: 40-70%",
            value: distribution?.poor || 0,
            color: "#FF9800",
          },
          {
            name: "Critical: <40%",
            value: distribution?.critical || 0,
            color: "#F44336",
          },
        ];

        setChartData(data);
        setDisplayData(data.filter((d) => d.value > 0));
      } else {
        setError(response.error || "Failed to load distribution data");
      }
    } catch (err: any) {
      setError(err.message || "Error fetching distribution data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchDistributionData(startDate, endDate);
  };

  const handleResetToDefault = (days: number = 30) => {
    const defaultStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const defaultEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  };

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              component="h3"
              sx={{ fontWeight: 600, color: "#8C1515", mb: 0 }}
            >
              Study Adherence Distribution
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{ minWidth: "auto", p: "4px 8px", color: "text.secondary" }}
              onClick={() => setShowUsageHint(true)}
            >
              How to use?
            </Button>
          </Box>

          <Dialog
            open={showUsageHint}
            onClose={() => setShowUsageHint(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
              How to use this graph
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" paragraph>
                  Understand if your adherence threshold is optimal. This helps
                  you evaluate whether threshold adjustments are needed to
                  better reflect realistic participant behavior.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key insights:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>
                    Check if the threshold is too strict - causing many
                    participants to fall into lower brackets
                  </li>
                  <li>Evaluate distribution across adherence levels</li>
                  <li>
                    Identify if the majority of participants are in the poor
                    adherence bucket
                  </li>
                  <li>
                    Use this to decide whether to adjust your adherence
                    thresholds
                  </li>
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
          >
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{ max: endDate }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{ min: startDate }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateRange}
            >
              Run
            </Button>
          </Stack>

          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {dateRangeError}
            </Alert>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(7)}
          >
            Last 7 Days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(15)}
          >
            Last 15 Days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(30)}
          >
            Last 30 Days
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !hasRun ? (
          <Alert severity="info">
            Select a date range and click Run to load this module.
          </Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : chartData.length > 0 ? (
          <Box>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const tooltipData = payload[0]
                        .payload as DistributionData;
                      return (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                          elevation={3}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ fontWeight: 600 }}
                          >
                            {tooltipData.name}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Participants: <strong>{tooltipData.value}</strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {totalParticipants > 0
                              ? `${((tooltipData.value / totalParticipants) * 100).toFixed(1)}% of total`
                              : "0% of total"}
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Pie
                  data={displayData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ value }) => (value ? `${value}` : "")}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {displayData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexWrap: "wrap",
                gap: 2.5,
                justifyContent: "center",
              }}
            >
              {chartData.map((item) => {
                const count =
                  displayData.find((d) => d.name === item.name)?.value || 0;
                return (
                  <Box
                    key={item.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: "150px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: item.color,
                        borderRadius: 1,
                        flexShrink: 0,
                      }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {count} {count === 1 ? "participant" : "participants"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Alert severity="info">
            No participants found for the selected date range.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StudyAdherenceDistribution;
